import { useState } from "react";
import { 
  Key, Zap, Cloud, Database, Globe, Mail, ExternalLink, 
  Eye, EyeOff, Edit2, Save, X, MessageCircle, Clock
} from "lucide-react";

const API = getApiUrl() + "/api";
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Configuration des tooltips et liens pour chaque connecteur
const CONNECTOR_INFO = {
  ringover: {
    tooltip: "Ringover gère la téléphonie VoIP et les SMS. Il synchronise automatiquement les appels entrants/sortants et permet d'envoyer des SMS aux clients.",
    links: [
      { label: "Dashboard Ringover", url: "https://app.ringover.com" },
      { label: "API Documentation", url: "https://developer.ringover.com" }
    ]
  },
  google_sheets: {
    tooltip: "Google Sheets sert de base de données principale pour les clients (onglet SUIVI CLIENTS). La connexion utilise un compte de service pour un accès sécurisé.",
    links: [
      { label: "Console Google Cloud", url: "https://console.cloud.google.com/apis/credentials" }
    ]
  },
  pennylane_read: {
    tooltip: "Pennylane (Lecture) permet de récupérer les factures et fournisseurs depuis votre comptabilité. Utilisé pour le rapprochement bancaire.",
    links: [
      { label: "Dashboard Pennylane", url: "https://app.pennylane.com" },
      { label: "API Pennylane", url: "https://developer.pennylane.com" }
    ]
  },
  pennylane_write: {
    tooltip: "Pennylane (Écriture) permettra de créer/modifier des factures. Nécessite un token avec permissions d'écriture.",
    links: [
      { label: "Dashboard Pennylane", url: "https://app.pennylane.com" },
      { label: "Demander accès écriture", url: "https://help.pennylane.com" }
    ]
  },
  google_ads_api: {
    tooltip: "Google Ads API récupère les données de vos campagnes publicitaires : dépenses, clics, impressions. Token actuellement en mode Test.",
    links: [
      { label: "Google Ads", url: "https://ads.google.com" },
      { label: "API Console", url: "https://console.cloud.google.com/apis/credentials" },
      { label: "Developer Token Status", url: "https://ads.google.com/aw/apicenter" }
    ]
  },
  gmail_imap: {
    tooltip: "Gmail IMAP se connecte à la boîte mail EnergoSym pour récupérer automatiquement les factures Google Ads reçues par email.",
    links: [
      { label: "Gmail", url: "https://mail.google.com" },
      { label: "Mots de passe d'application", url: "https://myaccount.google.com/apppasswords" }
    ]
  },
  whatsapp: {
    tooltip: "WhatsApp Business API permet d'envoyer et recevoir des messages WhatsApp automatisés aux clients. Le token actuel est temporaire et en attente de validation du numéro par Meta.",
    links: [
      { label: "Meta Business Suite", url: "https://business.facebook.com" },
      { label: "WhatsApp Business Platform", url: "https://developers.facebook.com/docs/whatsapp" },
      { label: "Gérer les numéros", url: "https://business.facebook.com/wa/manage/phone-numbers" }
    ]
  },
  twilio_sms: {
    tooltip: "Twilio SMS permet d'envoyer des SMS automatiques aux clients et techniciens : notifications de nouveaux leads, confirmations de RDV, rappels J-1, et alertes équipe.",
    links: [
      { label: "Console Twilio", url: "https://console.twilio.com" },
      { label: "Logs SMS", url: "https://console.twilio.com/us1/monitor/logs/sms" },
      { label: "Documentation API", url: "https://www.twilio.com/docs/sms" }
    ]
  },
  emergent_llm: {
    tooltip: "Emergent LLM donne accès à plusieurs modèles d'IA : GPT-4, Claude, et Gemini. Utilisé par SAN-IA pour répondre aux questions.",
    links: [
      { label: "Emergent Platform", url: "https://emergentagent.com" }
    ]
  },
  mongodb: {
    tooltip: "MongoDB stocke les appels, SMS, factures importées, configurations, tokens OAuth, et données de rapprochement bancaire.",
    links: [
      { label: "MongoDB Documentation", url: "https://www.mongodb.com/docs" }
    ]
  }
};

function ConnectorIcon({ type, status, integId }) {
  // Couleur spéciale pour le statut "pending"
  let color = 'text-red-400';
  if (status === 'connected') color = 'text-green-400';
  else if (status === 'pending') color = 'text-amber-400';
  
  const size = "w-5 h-5";
  
  // Icône spéciale pour WhatsApp
  if (integId === 'whatsapp') {
    return <MessageCircle className={`${size} ${color}`} />;
  }
  
  switch(type) {
    case 'api_key': return <Key className={`${size} ${color}`} />;
    case 'api_token': return <Zap className={`${size} ${color}`} />;
    case 'service_account': return <Cloud className={`${size} ${color}`} />;
    case 'database': return <Database className={`${size} ${color}`} />;
    case 'oauth': return <Globe className={`${size} ${color}`} />;
    case 'credentials': return <Mail className={`${size} ${color}`} />;
    default: return <Key className={`${size} ${color}`} />;
  }
}

function ConnectorCard({ integ, onSave }) {
  const [showKey, setShowKey] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const info = CONNECTOR_INFO[integ.id] || { tooltip: integ.description, links: [] };
  const isConnected = integ.status === 'connected';
  const isPending = integ.status === 'pending';
  
  // Couleur de fond selon le statut
  let bgClass = 'bg-red-500/5 border-red-500/20';
  if (isConnected) bgClass = 'bg-slate-800/50 border-slate-700/50';
  else if (isPending) bgClass = 'bg-amber-500/5 border-amber-500/20';

  // Ajouter le lien vers le spreadsheet si c'est Google Sheets
  const links = integ.id === 'google_sheets' && integ.extra_info?.spreadsheet_id
    ? [{ label: "Ouvrir le Spreadsheet", url: `https://docs.google.com/spreadsheets/d/${integ.extra_info.spreadsheet_id}` }, ...info.links]
    : info.links;

  // État supplémentaire pour WhatsApp (Phone ID)
  const [editPhoneId, setEditPhoneId] = useState("");

  const handleSave = async () => {
    try {
      // Pour WhatsApp, on envoie les deux valeurs
      if (integ.id === 'whatsapp') {
        // Sauvegarder le token
        if (editValue) {
          await authFetch(`${API}/admin/integrations/update`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ integration_id: 'whatsapp_token', value: editValue })
          });
        }
        // Sauvegarder le Phone ID
        if (editPhoneId) {
          await authFetch(`${API}/admin/integrations/update`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ integration_id: 'whatsapp_phone_id', value: editPhoneId })
          });
        }
      } else {
        await authFetch(`${API}/admin/integrations/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ integration_id: integ.id, value: editValue })
        });
      }
      if (onSave) onSave();
    } catch (err) {
      console.error("Error saving:", err);
    }
    setEditing(false);
    setEditValue("");
    setEditPhoneId("");
  };
  
  // Couleur de l'icône selon le statut
  let iconBgClass = 'bg-red-500/20';
  if (isConnected) iconBgClass = 'bg-green-500/20';
  else if (isPending) iconBgClass = 'bg-amber-500/20';

  return (
    <div className={`rounded-xl border p-5 ${bgClass}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className={`w-12 h-12 rounded-xl flex items-center justify-center cursor-help ${iconBgClass}`}
            title={info.tooltip}
          >
            <ConnectorIcon type={integ.type} status={integ.status} integId={integ.id} />
          </div>
          <div>
            <h4 className="font-semibold cursor-help" title={info.tooltip}>{integ.name}</h4>
            <p className="text-sm text-slate-400">{integ.description}</p>
            
            {integ.extra_info?.permissions && (
              <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${integ.extra_info.active ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                {integ.extra_info.permissions} {integ.extra_info.active ? '(actif)' : '(inactif)'}
              </span>
            )}
            
            {links.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {links.map((link, idx) => (
                  <a 
                    key={idx}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 bg-blue-500/10 px-2 py-1 rounded hover:bg-blue-500/20 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
              isConnected ? 'bg-green-500/20 text-green-400' : 
              isPending ? 'bg-amber-500/20 text-amber-400' : 
              'bg-red-500/20 text-red-400'
            }`}>
              {isPending && <Clock className="w-3 h-3" />}
              {isConnected ? 'Connecté' : isPending ? 'En attente' : 'Non configuré'}
            </span>
            <p className="text-xs text-slate-500 mt-1 font-mono">
              {showKey ? integ.key_preview : '••••••••••••'}
            </p>
          </div>
          <button onClick={() => setShowKey(!showKey)} className="p-2 hover:bg-slate-700 rounded-lg">
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          {integ.editable && (
            <button onClick={() => { setEditing(true); setEditValue(""); }} className="p-2 hover:bg-slate-700 rounded-lg text-blue-400">
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Détails supplémentaires */}
      {integ.extra_info && !integ.extra_info.permissions && (
        <div className="mt-4 pt-4 border-t border-slate-700/50 grid grid-cols-2 gap-4 text-sm">
          {Object.entries(integ.extra_info).map(([key, value]) => (
            <div key={key} className={key === 'collections' || key === 'token_status' ? 'col-span-2' : ''}>
              <p className="text-slate-500 text-xs mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
              {key === 'token_status' ? (
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">{value}</span>
              ) : (
                <p className="font-mono text-slate-300 text-xs break-all">{value}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edition */}
      {editing && (
        <div className="mt-4 pt-4 border-t border-slate-700">
          {integ.id === 'whatsapp' ? (
            /* Edition spéciale pour WhatsApp avec 2 champs */
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Phone Number ID</label>
                <input 
                  type="text" 
                  value={editPhoneId} 
                  onChange={(e) => setEditPhoneId(e.target.value)} 
                  placeholder={integ.extra_info?.phone_number_id || "Nouveau Phone Number ID..."} 
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm" 
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Token d'accès</label>
                <input 
                  type="password" 
                  value={editValue} 
                  onChange={(e) => setEditValue(e.target.value)} 
                  placeholder="Nouveau token WhatsApp..." 
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm" 
                />
              </div>
              <p className="text-xs text-amber-400">💡 Laissez vide les champs que vous ne souhaitez pas modifier</p>
              <div className="flex gap-3">
                <button onClick={handleSave} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2">
                  <Save className="w-4 h-4" /> Sauver
                </button>
                <button onClick={() => { setEditing(false); setEditPhoneId(""); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg">
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            /* Edition standard pour les autres connecteurs */
            <div className="flex gap-3">
              <input 
                type={integ.id === 'google_sheets' ? 'text' : 'password'} 
                value={editValue} 
                onChange={(e) => setEditValue(e.target.value)} 
                placeholder={integ.id === 'google_sheets' ? 'Nouveau Spreadsheet ID...' : `Nouveau token...`} 
                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 font-mono text-sm" 
              />
              <button onClick={handleSave} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2">
                <Save className="w-4 h-4" /> Sauver
              </button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg">
                Annuler
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConnectorsList({ integrations, onRefresh }) {
  return (
    <div className="space-y-4">
      {integrations.map(integ => (
        <ConnectorCard key={integ.id} integ={integ} onSave={onRefresh} />
      ))}
    </div>
  );
}
